import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe, JsonPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { CopyId } from '@/app/atoms/copy-id/copy-id';
import { ImageService } from '@/app/services/image/image.service';
import { AdminCancellationsService } from '../../services/admin-cancellations.service';
import {
  formatMinorUnits,
  outcomeChipClass,
  outcomeLabel,
  scopeLabel,
} from '../../cancellations.helpers';
import { CancellationAnswer, CancellationResponseDetail } from '../../cancellations.types';

/**
 * One exit survey in full.
 *
 * The answers are rendered from the stored rows, which snapshot the question and
 * option wording that was on screen when they were given — never from the
 * current `offboarding-survey.definition.ts`. Survey copy gets rewritten, and a
 * report read after a rewrite has to show what the brand was actually asked.
 */
@Component({
  selector: 'admin-cancellation-detail-drawer',
  imports: [DatePipe, JsonPipe, RouterLink, LdgImgComponent, LoadingInlineComponent, CopyId],
  templateUrl: './cancellation-detail-drawer.html',
  styleUrl: './cancellation-detail-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'close()',
  },
})
export class CancellationDetailDrawer implements OnDestroy {
  readonly responseId = input.required<string>();
  readonly closed = output<void>();

  readonly detail = signal<CancellationResponseDetail | null>(null);
  readonly loadingState = signal<'loading' | 'loaded' | 'error'>('loading');
  readonly error = signal<string | null>(null);

  readonly outcomeChipClass = outcomeChipClass;
  readonly outcomeLabel = outcomeLabel;
  readonly scopeLabel = scopeLabel;
  readonly imageService = inject(ImageService);

  /** Only the chip answers are grouped; the comment reads better on its own. */
  readonly listedAnswers = computed<CancellationAnswer[]>(
    () => this.detail()?.answers.filter((answer) => answer.questionId !== 'comment') ?? [],
  );

  readonly commentAnswer = computed<CancellationAnswer | null>(
    () => this.detail()?.answers.find((answer) => answer.questionId === 'comment') ?? null,
  );

  readonly profileLink = computed<unknown[] | null>(() => {
    const account = this.detail()?.account;
    if (!account?.businessAccountId) return null;
    return ['/admin-options/business-profile', account.businessAccountId];
  });

  readonly userLink = computed<unknown[] | null>(() => {
    const uid = this.detail()?.account.userUid;
    return uid ? ['/admin-options/user-profile', uid] : null;
  });

  private loadSubscription = new Subscription();
  private readonly cancellationsService = inject(AdminCancellationsService);

  constructor() {
    effect(() => {
      const id = this.responseId();
      this.loadSubscription.unsubscribe();
      this.loadSubscription = new Subscription();
      this.detail.set(null);
      this.loadingState.set('loading');
      this.error.set(null);

      this.loadSubscription.add(
        this.cancellationsService.getResponse(id).subscribe({
          next: (detail) => {
            this.detail.set(detail);
            this.loadingState.set('loaded');
          },
          error: (requestError: HttpErrorResponse) => {
            this.error.set(
              requestError.status === 403
                ? 'GodMode access is required to read an individual exit survey.'
                : (requestError.error?.message ?? 'Could not load this response'),
            );
            this.loadingState.set('error');
          },
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription.unsubscribe();
  }

  close(): void {
    this.closed.emit();
  }

  money(minorUnits: number | null): string {
    return formatMinorUnits(minorUnits, this.detail()?.currencyCode ?? null);
  }

  /** Renewal reads as a rate, because a yearly plan next to a monthly one does not. */
  renewalLabel(): string {
    const detail = this.detail();
    if (!detail || detail.renewalAmountMinorUnits === null) return '—';

    const amount = formatMinorUnits(detail.renewalAmountMinorUnits, detail.currencyCode);
    const count = detail.billingIntervalCount ?? 1;
    const interval = detail.billingInterval ?? 'month';
    const unit = count > 1 ? `${count} ${interval}s` : interval;

    return `${amount} / ${unit}`;
  }

  answerText(answer: CancellationAnswer): string {
    return answer.answerLabel ?? answer.answerValue;
  }
}
